class CartLinePurchaseOption extends HTMLElement {
  constructor() {
    super();
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.previousValue = this.dataset.currentValue || this.select.value;
    this.select.addEventListener('change', this.onSelectChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.onSelectChange);
  }

  onSelectChange(event) {
    event.stopPropagation();

    const option = this.select.selectedOptions[0];
    if (!option || option.value === this.previousValue) return;

    const lineIndex = Number(this.dataset.lineIndex);
    const quantity = Number(this.dataset.quantity);
    const variantId = option.dataset.variantId;
    const sellingPlanId = option.dataset.sellingPlanId || null;

    if (!lineIndex || !quantity || !variantId) {
      this.select.value = this.previousValue;
      return;
    }

    this.safeReplaceLine(lineIndex, quantity, variantId, sellingPlanId, option.value);
  }

  safeReplaceLine(lineIndex, quantity, variantId, sellingPlanId, newValue) {
    this.setLoading(lineIndex, true);
    let snapshot = null;

    this.captureCartSnapshot()
      .then((snap) => {
        snapshot = snap;
        return this.changeLineToZero(lineIndex);
      })
      .then(() => {
        const item = { id: Number(variantId), quantity };
        if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
        return this.addCartItem(item);
      })
      .then((addState) => {
        const sectionConfig = this.getSectionConfig();
        this.renderCart(addState, sectionConfig);
        this.previousValue = newValue;
        this.dataset.currentVariantId = String(variantId);
        if (sellingPlanId) {
          this.dataset.currentSellingPlanId = String(sellingPlanId);
        } else {
          delete this.dataset.currentSellingPlanId;
        }
        window.RuCartErrors?.clearPurchaseOptionError(this);
        this.refreshRecommendations();

        return fetch('/cart.js')
          .then((response) => response.json())
          .then((cartData) => {
            this.publishCartUpdate(cartData, variantId);
          });
      })
      .catch((error) =>
        this.restoreCartSnapshot(snapshot).finally(() => {
          this.handleError(error);
        })
      )
      .finally(() => this.setLoading(lineIndex, false));
  }

  captureCartSnapshot() {
    return fetch('/cart.js').then((response) => response.json());
  }

  changeLineToZero(lineIndex) {
    return fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
      ...fetchConfig(),
      body: JSON.stringify({ line: lineIndex, quantity: 0 }),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message);
        if (state.errors) throw new Error(state.errors);
        return state;
      });
  }

  restoreCartSnapshot(snapshot) {
    if (!snapshot?.items?.length) {
      return this.refreshCartFromServer();
    }

    return fetch('/cart.js')
      .then((response) => response.json())
      .then((current) => {
        const missing = snapshot.items.filter((snapItem) => {
          const snapPlanId = snapItem.selling_plan_allocation?.selling_plan?.id || null;
          const match = current.items.find((curItem) => {
            const curPlanId = curItem.selling_plan_allocation?.selling_plan?.id || null;
            return curItem.variant_id === snapItem.variant_id && curPlanId === snapPlanId;
          });
          return !match || match.quantity < snapItem.quantity;
        });

        if (!missing.length) {
          return this.refreshCartFromServer();
        }

        const items = missing.map((item) => {
          const payload = { id: item.variant_id, quantity: item.quantity };
          if (item.selling_plan_allocation?.selling_plan?.id) {
            payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
          }
          return payload;
        });

        const sectionConfig = this.getSectionConfig();
        const addPayload = { items };

        if (sectionConfig) {
          addPayload.sections = sectionConfig.sections;
          addPayload.sections_url = sectionConfig.sections_url;
        }

        return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
          ...fetchConfig(),
          body: JSON.stringify(addPayload),
        })
          .then((response) => response.json())
          .then((state) => {
            this.renderCart(state, sectionConfig);
            return state;
          });
      })
      .catch((error) => {
        console.error(error);
        return this.refreshCartFromServer();
      });
  }

  refreshCartFromServer() {
    const sectionConfig = this.getSectionConfig();
    if (!sectionConfig) return Promise.resolve();

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    if (sectionConfig.type === 'drawer' && sectionConfig.drawer) {
      return fetch(`${cartUrl}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newDrawer = doc.querySelector('cart-drawer');
          if (!newDrawer || !sectionConfig.drawer.parentNode) return;
          sectionConfig.drawer.innerHTML = newDrawer.innerHTML;
        });
    }

    if (sectionConfig.type === 'page' && sectionConfig.cartItems) {
      return fetch(`${cartUrl}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const sourceCartItems = doc.querySelector('cart-items');
          if (sourceCartItems) sectionConfig.cartItems.innerHTML = sourceCartItems.innerHTML;
        });
    }

    return Promise.resolve();
  }

  addCartItem(item) {
    const addPayload = { items: [item] };
    const sectionConfig = this.getSectionConfig();

    if (sectionConfig) {
      addPayload.sections = sectionConfig.sections;
      addPayload.sections_url = sectionConfig.sections_url;
    }

    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      ...fetchConfig(),
      body: JSON.stringify(addPayload),
    })
      .then((response) => response.json())
      .then((addState) => {
        if (addState.status) throw new Error(addState.description || addState.message);
        if (addState.errors) throw new Error(addState.errors);
        return addState;
      });
  }

  publishCartUpdate(cartData, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-line-purchase-option',
        cartData,
        variantId: String(variantId),
      });
    }
  }

  handleError(error) {
    console.error(error);
    this.select.value = this.previousValue;

    if (window.RuCartErrors?.isCartCorruptionError(error)) {
      window.RuCartErrors.handlePurchaseOptionFailure(this, error);
      return;
    }

    const message =
      (window.RuCartErrors && window.RuCartErrors.resolve(error)) ||
      error?.message ||
      window.cartStrings?.error ||
      '';

    if (window.RuCartErrors) {
      window.RuCartErrors.showPurchaseOptionError(this, message);
      return;
    }

    const errors =
      document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
    window.alert(message);
  }

  setLoading(lineIndex, isLoading) {
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    this.select.disabled = isLoading;

    if (!cartItems) return;
    if (isLoading && cartItems.enableLoading) cartItems.enableLoading(lineIndex);
    if (!isLoading && cartItems.disableLoading) cartItems.disableLoading(lineIndex);
  }

  getSectionConfig() {
    if (this.closest('cart-drawer-items')) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return {
          type: 'drawer',
          sections: drawer.getSectionsToRender().map((section) => section.id).join(','),
          sections_url: window.location.pathname,
          drawer,
        };
      }
    }

    const cartItems = this.closest('cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return {
        type: 'page',
        sections: cartItems.getSectionsToRender().map((section) => section.section).join(','),
        sections_url: window.location.pathname,
        cartItems,
      };
    }

    return null;
  }

  renderCart(parsedState, sectionConfig) {
    if (!sectionConfig) return;

    if (sectionConfig.type === 'drawer') {
      if (!parsedState.sections || !sectionConfig.drawer?.renderContents) return;

      if (sectionConfig.drawer.classList.contains('is-empty')) {
        sectionConfig.drawer.classList.remove('is-empty');
      }
      sectionConfig.drawer.renderContents(parsedState);
      return;
    }

    if (sectionConfig.type === 'page' && sectionConfig.cartItems?.getSectionsToRender) {
      this.renderCartPage(parsedState, sectionConfig.cartItems);
    }
  }

  renderCartPage(parsedState, cartItems) {
    const itemCount = parsedState.item_count ?? parsedState.items?.length ?? 0;
    cartItems.classList.toggle('is-empty', itemCount === 0);

    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    if (parsedState.sections) {
      cartItems.getSectionsToRender().forEach((section) => {
        const sectionHtml = parsedState.sections[section.section];
        if (!sectionHtml) return;

        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!elementToReplace) return;

        elementToReplace.innerHTML = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
      });
      return;
    }

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${cartUrl}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceCartItems = html.querySelector('cart-items');
        if (sourceCartItems) cartItems.innerHTML = sourceCartItems.innerHTML;
      })
      .catch((error) => console.error(error));

    if (cartFooter) {
      fetch(`${cartUrl}?section_id=main-cart-footer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceFooter = html.querySelector('.js-contents');
          const targetFooter = cartFooter.querySelector('.js-contents');
          if (sourceFooter && targetFooter) targetFooter.innerHTML = sourceFooter.innerHTML;
        })
        .catch((error) => console.error(error));
    }
  }

  refreshRecommendations() {
    const host = document.querySelector('cart-drawer-recommendations');
    if (!host) return;

    fetch('/cart.js')
      .then((res) => res.json())
      .then((cartData) => {
        if (!cartData.items?.length) {
          host.hide?.();
          return;
        }
        host.dataset.productId = String(cartData.items[0].product_id);
        host.tryLoad?.();
      })
      .catch(() => host.tryLoad?.());
  }
}

customElements.define('cart-line-purchase-option', CartLinePurchaseOption);
